import {
  timelineUseCases as defaultTimelineUseCases,
  type TimelineData,
  type TimelineEditorState,
} from '@/features/timeline';
import type { TimelineRecord } from '@/features/timeline/model/timeline-record';
import {
  TimelineNotFoundError,
} from '@/features/timeline/application/timeline-errors';
import { startTransition, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/shared/i18n/i18n';
import { useTimelineEditorActions } from './use-timeline-editor-actions';
import { useTimelineEditorShortcuts } from './use-timeline-editor-shortcuts';

type TimelineDetailErrorCode = 'not-found' | 'error' | null;

interface TimelineDetailController {
  timelineData: TimelineData | null;
  canUndo: boolean;
  canRedo: boolean;
  loading: boolean;
  error: string | null;
  errorCode: TimelineDetailErrorCode;
  isDirty: boolean;
  retry: () => void;
  addTimelineSection: () => void;
  handleSectionDelete: (sectionId: string) => void;
  handleSectionMove: (sectionId: string, toIndex: number) => void;
  handleSectionLabelChange: (sectionId: string, nextLabel: string) => void;
  handleItemTitleChange: (sectionId: string, itemId: string, nextTitle: string) => void;
  handleItemCreate: (sectionId: string, draft: { title: string; startDate: string; endDate?: string }) => void;
  handleItemDateRangeChange: (
    itemId: string,
    nextRange: { sectionId: string; startDate: string; endDate?: string },
  ) => void;
  handleItemDelete: (sectionId: string, itemId: string) => void;
  handleSectionVisibilityToggle: (sectionId: string) => void;
  handleSave: () => Promise<void>;
  handleSaveAs: () => Promise<void>;
  fileName: string | null;
}

type OnOptimistic = (state: TimelineEditorState) => void;

interface TimelineDetailUseCases {
  createEditorSession: (id: string) => Promise<TimelineEditorState | null>;
  refreshEditorSession: (id: string) => Promise<TimelineEditorState | null>;
  disposeEditorSession: (id: string) => void;
  addSection: (id: string, onOptimistic?: OnOptimistic) => Promise<TimelineEditorState | null>;
  removeSection: (id: string, sectionId: string, onOptimistic?: OnOptimistic) => Promise<TimelineEditorState | null>;
  moveSection: (id: string, sectionId: string, toIndex: number, onOptimistic?: OnOptimistic) => Promise<TimelineEditorState | null>;
  updateSectionLabel: (
    id: string,
    sectionId: string,
    nextLabel: string,
    onOptimistic?: OnOptimistic,
  ) => Promise<TimelineEditorState | null>;
  updateItemTitle: (
    id: string,
    sectionId: string,
    itemId: string,
    nextTitle: string,
    onOptimistic?: OnOptimistic,
  ) => Promise<TimelineEditorState | null>;
  createItem: (
    id: string,
    sectionId: string,
    draft: { title: string; startDate: string; endDate?: string },
    onOptimistic?: OnOptimistic,
  ) => Promise<TimelineEditorState | null>;
  changeItemDateRange: (
    id: string,
    itemId: string,
    nextRange: { sectionId: string; startDate: string; endDate?: string },
    onOptimistic?: OnOptimistic,
  ) => Promise<TimelineEditorState | null>;
  hideSection: (
    id: string,
    sectionId: string,
    onOptimistic?: OnOptimistic,
  ) => Promise<TimelineEditorState | null>;
  showSection: (
    id: string,
    sectionId: string,
    onOptimistic?: OnOptimistic,
  ) => Promise<TimelineEditorState | null>;
  deleteItem: (
    id: string,
    sectionId: string,
    itemId: string,
    onOptimistic?: OnOptimistic,
  ) => Promise<TimelineEditorState | null>;
  undo: (id: string, onOptimistic?: OnOptimistic) => Promise<TimelineEditorState | null>;
  redo: (id: string, onOptimistic?: OnOptimistic) => Promise<TimelineEditorState | null>;
  saveFile: (id: string, handle?: FileSystemFileHandle) => Promise<{ fileName: string; handle: FileSystemFileHandle } | null>;
  saveFileAs: (id: string, suggestedName?: string) => Promise<{ fileName: string; handle: FileSystemFileHandle } | null>;
  getFileHandle?: (id: string) => FileSystemFileHandle | undefined;
  openById?: (id: string) => Promise<{ record: TimelineRecord; fileName: string; handle: FileSystemFileHandle } | null>;
  startFileWatch?: (id: string, onChange: (record: TimelineRecord) => void) => void;
  stopFileWatch?: (id: string) => void;
}

const getErrorMeta = (error: unknown): { code: TimelineDetailErrorCode; message: string } => {
  if (error instanceof TimelineNotFoundError) {
    return { code: 'not-found', message: error.message };
  }
  return { code: 'error', message: i18n.t('error.loadFailed', { ns: 'timeline' }) };
};

export const useTimelineDetailController = (
  timelineId: string | undefined,
  enabled = true,
  useCases: TimelineDetailUseCases = defaultTimelineUseCases,
): TimelineDetailController => {
  const { t } = useTranslation('timeline');

  const [timelineData, setTimelineData] = useState<TimelineData | null>(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<TimelineDetailErrorCode>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const initialLoadDoneRef = useRef(false);
  const fileHandleRef = useRef<FileSystemFileHandle | null>(null);
  // --- Auto-save (debounced) ---

  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const AUTO_SAVE_DELAY = 500;

  const scheduleAutoSave = useCallback(() => {
    if (!timelineId || !fileHandleRef.current) return;

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    const handle = fileHandleRef.current;
    autoSaveTimerRef.current = setTimeout(() => {
      void useCases.saveFile(timelineId, handle).then(() => {
        setIsDirty(false);
      }).catch((err) => {
        console.error(t('error.autoSaveFailed'), err);
      });
    }, AUTO_SAVE_DELAY);
  }, [timelineId, useCases]);

  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  const applyEditorState = useCallback((nextState: TimelineEditorState | null) => {
    if (!nextState) {
      return;
    }

    startTransition(() => {
      setTimelineData(nextState.data);
      setCanUndo(nextState.canUndo);
      setCanRedo(nextState.canRedo);
    });

    if (initialLoadDoneRef.current) {
      setIsDirty(true);
    }

    scheduleAutoSave();
  }, [scheduleAutoSave]);

  // --- Session lifecycle ---

  const loadEditorSession = useCallback(async () => {
    if (!timelineId) {
      setErrorCode('error');
      setError(t('error.noTimelineId'));
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const editorState = await useCases.refreshEditorSession(timelineId);
      if (!editorState) {
        setErrorCode('not-found');
        setError(t('error.timelineNotFound'));
        setTimelineData(null);
        return;
      }

      applyEditorState(editorState);
      initialLoadDoneRef.current = true;
      setError(null);
      setErrorCode(null);
    } catch (err) {
      const meta = getErrorMeta(err);
      setErrorCode(meta.code);
      setError(meta.message);
      setTimelineData(null);
    } finally {
      setLoading(false);
    }
  }, [applyEditorState, timelineId, useCases]);

  useEffect(() => {
    if (!enabled) {
      startTransition(() => {
        setTimelineData(null);
        setCanUndo(false);
        setCanRedo(false);
        setLoading(false);
        setError(null);
        setErrorCode(null);
      });
      return;
    }

    let disposed = false;

    const load = async () => {
      if (!timelineId) {
        setErrorCode('error');
        setError(t('error.noTimelineId'));
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      setErrorCode(null);

      try {
        // openFile 経由で開かれた場合、UseCases に保持されたハンドルを引き継ぐ
        const existingHandle = useCases.getFileHandle?.(timelineId);
        if (existingHandle) {
          fileHandleRef.current = existingHandle;
          setFileName(existingHandle.name);
        }

        let editorState = await useCases.createEditorSession(timelineId);
        if (disposed) {
          return;
        }
        // リポジトリに無い場合、IndexedDB のハンドルからファイルを復元する
        if (!editorState && useCases.openById) {
          const opened = await useCases.openById(timelineId);
          if (disposed) return;
          if (opened) {
            fileHandleRef.current = opened.handle;
            setFileName(opened.fileName);
            editorState = await useCases.createEditorSession(timelineId);
            if (disposed) return;
          }
        }
        if (!editorState) {
          setErrorCode('not-found');
          setError(t('error.timelineNotFound'));
          setLoading(false);
          return;
        }

        applyEditorState(editorState);
        initialLoadDoneRef.current = true;
      } catch (err) {
        if (disposed) {
          return;
        }
        const meta = getErrorMeta(err);
        setErrorCode(meta.code);
        setError(meta.message);
      } finally {
        if (!disposed) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      disposed = true;
      if (timelineId) {
        useCases.disposeEditorSession(timelineId);
      }
    };
  }, [applyEditorState, enabled, timelineId, useCases]);

  // --- File watch (detect external changes) ---

  const handleExternalChange = useCallback(
    async (record: TimelineRecord) => {
      if (!timelineId) return;
      const editorState = await useCases.refreshEditorSession(timelineId);
      if (editorState) {
        startTransition(() => {
          setTimelineData(editorState.data);
          setCanUndo(editorState.canUndo);
          setCanRedo(editorState.canRedo);
        });
      }
    },
    [timelineId, useCases],
  );

  useEffect(() => {
    if (!enabled || !timelineId || !fileName) return;
    if (!useCases.startFileWatch || !useCases.stopFileWatch) return;

    useCases.startFileWatch(timelineId, (record) => {
      void handleExternalChange(record);
    });

    return () => {
      useCases.stopFileWatch!(timelineId);
    };
  }, [enabled, timelineId, fileName, useCases, handleExternalChange]);

  // --- File save actions ---

  const handleSave = useCallback(async () => {
    if (!timelineId) return;
    try {
      const result = await useCases.saveFile(timelineId, fileHandleRef.current ?? undefined);
      if (result) {
        fileHandleRef.current = result.handle;
        setFileName(result.fileName);
        setIsDirty(false);
      }
    } catch (err) {
      console.error(t('error.saveFailed'), err);
    }
  }, [timelineId, useCases]);

  const handleSaveAs = useCallback(async () => {
    if (!timelineId) return;
    try {
      const result = await useCases.saveFileAs(timelineId, fileName ?? undefined);
      if (result) {
        fileHandleRef.current = result.handle;
        setFileName(result.fileName);
        setIsDirty(false);
      }
    } catch (err) {
      console.error(t('error.saveFailed'), err);
    }
  }, [timelineId, useCases]);

  // --- beforeunload (unsaved changes warning) ---

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // --- Ctrl+S shortcut ---

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (e.shiftKey) {
          void handleSaveAs();
        } else {
          void handleSave();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleSave, handleSaveAs]);

  // --- Editor actions (delegated) ---

  const actions = useTimelineEditorActions({
    timelineId,
    timelineData,
    canUndo,
    canRedo,
    applyEditorState,
    setError,
    setErrorCode,
    setTimelineData,
    useCases,
  });

  // --- Keyboard shortcuts ---

  useTimelineEditorShortcuts({
    canUndo,
    canRedo,
    onUndo: actions.undoTimelineEdit,
    onRedo: actions.redoTimelineEdit,
  });

  return {
    timelineData,
    canUndo,
    canRedo,
    loading,
    error,
    errorCode,
    isDirty,
    fileName,
    retry: () => {
      void loadEditorSession();
    },
    addTimelineSection: actions.addTimelineSection,
    handleSectionDelete: actions.handleSectionDelete,
    handleSectionMove: actions.handleSectionMove,
    handleSectionLabelChange: actions.handleSectionLabelChange,
    handleSectionVisibilityToggle: actions.handleSectionVisibilityToggle,
    handleItemTitleChange: actions.handleItemTitleChange,
    handleItemCreate: actions.handleItemCreate,
    handleItemDateRangeChange: actions.handleItemDateRangeChange,
    handleItemDelete: actions.handleItemDelete,
    handleSave,
    handleSaveAs,
  };
};
